import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Moon, Sunrise, ArrowRight, Lock, Loader2, RefreshCw } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/api";

const Big = ({ icon: I, label, p, i }) => (
  <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.12 }}
    className="mc-card p-5 sm:p-6 text-center" data-testid={`snapshot-${label.toLowerCase().replace(/ .*/, "")}`}>
    <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300"><I size={20} /></span>
    <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className="mt-1 font-serif text-2xl sm:text-3xl text-amber-100">{p?.sign || "—"}</p>
    <p className="text-xs text-slate-500">{p ? `${p.sign_vedic} · ${p.degree}°` : ""}</p>
  </motion.div>
);

function PlanetTable({ planets }) {
  return (
    <div className="mc-card p-4 sm:p-6 mt-6" data-testid="snapshot-planets">
      <p className="text-xs uppercase tracking-[0.15em] text-amber-300 mb-3">Key Planetary Positions</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {planets.map((p) => (
          <div key={p.name} className="flex items-center justify-between rounded-lg bg-[#0B1024]/70 border border-amber-500/10 px-3 py-2 text-sm" data-testid={`snapshot-planet-${p.name.toLowerCase()}`}>
            <span className="text-slate-200">{p.name}{p.retro && <span className="ml-1 text-[10px] text-purple-300">(R)</span>}</span>
            <span className="text-amber-200">{p.sign}{p.house ? <span className="text-slate-500 text-xs"> · H{p.house}</span> : null}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Welcome() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [subActive, setSubActive] = useState(false);
  const load = () => {
    setErr(""); setD(null);
    api.get("/chart/preview").then((r) => setD(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your chart"));
  };
  useEffect(() => {
    load();
    api.get("/subscription").then((r) => setSubActive(r.data?.status === "active")).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#080B1A] text-slate-100">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-24 sm:pt-28 pb-16" data-testid="welcome-page">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-400/90 text-center">Your Birth Chart Snapshot</p>
        <h1 className="mt-3 font-serif text-3xl sm:text-5xl text-center text-slate-50">
          {d ? <>Welcome, <span className="gold-text">{d.name.split(" ")[0]}</span></> : "Reading the stars…"}
        </h1>
        {d && <p className="mt-3 text-center text-sm text-slate-400" data-testid="snapshot-birth">Born {d.birth.date}{d.birth.time ? ` at ${d.birth.time}` : ""} · {d.birth.place}</p>}

        {!d && !err && (
          <div className="mt-14 flex flex-col items-center text-slate-400" data-testid="snapshot-loading">
            <Loader2 className="animate-spin text-amber-300" size={30} /><p className="mt-3 text-sm">Calculating your Vedic chart…</p>
          </div>
        )}
        {err && (
          <div className="mt-10 mc-card p-6 text-center" data-testid="snapshot-error">
            <p className="text-sm text-slate-300">{err}</p>
            <div className="mt-4 flex justify-center gap-3">
              <button onClick={load} className="btn-ghost-gold rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"><RefreshCw size={14} /> Try again</button>
              <button onClick={() => nav("/onboarding")} className="btn-gold rounded-full px-5 py-2 text-sm">Update birth details</button>
            </div>
          </div>
        )}

        {d && (
          <>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Big icon={Sunrise} label="Ascendant (Lagna)" p={d.ascendant} i={0} />
              <Big icon={Moon} label="Moon Sign (Rashi)" p={d.moon} i={1} />
              <Big icon={Sun} label="Sun Sign (Vedic)" p={d.sun} i={2} />
            </div>
            <PlanetTable planets={d.planets} />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="relative mt-8 p-[1px] rounded-3xl bg-gradient-to-br from-amber-400/60 via-amber-200/10 to-purple-500/40">
              <div className="rounded-3xl bg-[#0E132A] p-6 sm:p-8 text-center">
                <Lock size={20} className="mx-auto text-amber-300" />
                <h2 className="mt-3 font-serif text-xl sm:text-2xl text-slate-50">What does this mean for you this month?</h2>
                <p className="mt-2 text-sm text-slate-400 max-w-xl mx-auto">
                  With your Moon in {d.moon?.sign} and {d.ascendant?.sign} rising, your monthly reading covers career, love, money, health
                  and remedies — written from this exact chart.
                </p>
                <button data-testid="snapshot-subscribe-btn" onClick={() => nav(subActive ? "/dashboard" : "/subscribe")}
                  className="btn-gold rounded-full px-7 py-3.5 mt-6 inline-flex items-center gap-2">
                  {subActive ? "Go to My Dashboard" : "Unlock My Monthly Reading"} <ArrowRight size={18} />
                </button>
                <button data-testid="snapshot-skip-btn" onClick={() => nav("/dashboard")} className="block mx-auto mt-3 text-xs text-slate-500 hover:text-slate-300">Maybe later</button>
              </div>
            </motion.div>
            <p className="mt-6 text-center text-[11px] text-slate-600">Calculated with Lahiri ayanamsha. Astrology is for guidance and reflection only.</p>
          </>
        )}
      </main>
    </div>
  );
}
